import { useEffect, useRef, useState } from 'react';
import { t } from '../../content';
import * as Constants from '../../constants';
import { askSupport } from '../../supportChat/askSupport';
import supportAssistantIllustration from '../../assets/images/illustrations/support-assistant.svg';

const SUPPORT_EMAIL = 'admin@apexteam.ai';

function SupportChat({ locale, route, roleId, companyActive, accessToken }) {
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState('');
    const [sending, setSending] = useState(false);
    const [messages, setMessages] = useState([]);
    const [conversationId, setConversationId] = useState(null);
    const threadRef = useRef(null);
    const abortRef = useRef(null);

    useEffect(() => {
        return () => abortRef.current?.abort();
    }, []);

    useEffect(() => {
        const thread = threadRef.current;
        if (thread) {
            thread.scrollTop = thread.scrollHeight;
        }
    }, [messages, sending, open]);

    const send = async (event) => {
        event.preventDefault();
        const message = draft.trim();
        const role = Number(roleId);
        if (!message || sending || !accessToken || !Number.isInteger(role)) {
            return;
        }
        setDraft('');
        setSending(true);
        setMessages((current) => [
            ...current,
            { id: `${Date.now()}-user`, role: 'user', text: message, articles: [] },
            { id: `${Date.now()}-assistant`, role: 'assistant', text: '', articles: [] },
        ]);
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;
        try {
            await askSupport({
                apiUrl: Constants.SUPPORT_API_URL,
                token: accessToken,
                signal: controller.signal,
                body: {
                    platform: 'corporate',
                    locale,
                    route: route || '/',
                    role_id: role,
                    company_active: Boolean(companyActive),
                    message,
                    conversation_id: conversationId,
                },
                onEvent: (sse) => {
                    if (sse.event === 'token') {
                        appendAssistant(sse.data.text || '');
                    } else if (sse.event === 'citation') {
                        setArticles(sse.data.articles || []);
                    } else if (sse.event === 'done' && sse.data.conversation_id) {
                        setConversationId(sse.data.conversation_id);
                    }
                },
            });
        } catch (error) {
            if (error.name !== 'AbortError') {
                appendAssistant(t('support.error'), { replaceEmpty: true });
            }
        } finally {
            setSending(false);
        }
    };

    return (
        <div className={`support-chat${open ? ' is-open' : ''}`}>
            {open && (
                <section className="support-chat-panel" aria-label={t('support.title')}>
                    <header className="support-chat-header">
                        <h2>{t('support.title')}</h2>
                        <button type="button" onClick={() => setOpen(false)}>{t('support.close')}</button>
                    </header>
                    <div className="support-chat-thread" ref={threadRef}>
                        {messages.length === 0 && (
                            <p className="support-chat-intro">{t('support.intro')}</p>
                        )}
                        {messages.map((item) => (
                            <article key={item.id} className={`support-chat-message is-${item.role}`}>
                                <p>{linkEmail(item.text) || (item.role === 'assistant' && sending ? t('support.thinking') : '')}</p>
                                {item.articles.length > 0 && (
                                    <p className="support-chat-sources">
                                        {t('support.sources')}: {item.articles.map((article) => article.title).join(', ')}
                                    </p>
                                )}
                            </article>
                        ))}
                    </div>
                    <form className="support-chat-form" onSubmit={send}>
                        <label className="support-chat-label" htmlFor="support-chat-input">{t('support.placeholder')}</label>
                        <input
                            id="support-chat-input"
                            value={draft}
                            onChange={(event) => setDraft(event.target.value)}
                            placeholder={t('support.placeholder')}
                            disabled={sending}
                            maxLength={2000}
                        />
                        <button type="submit" disabled={sending || !draft.trim()}>{t('support.send')}</button>
                    </form>
                </section>
            )}
            <button
                type="button"
                className="support-chat-launcher"
                aria-expanded={open}
                onClick={() => setOpen((current) => !current)}
            >
                {open ? t('support.close') : (
                    <>
                        <img
                            className="support-chat-launcher-art"
                            src={supportAssistantIllustration}
                            alt=""
                        />
                        <span>{t('support.open')}</span>
                    </>
                )}
            </button>
        </div>
    );

    function appendAssistant(text, { replaceEmpty = false } = {}) {
        setMessages((current) => {
            const next = [...current];
            const last = next[next.length - 1];
            if (!last || last.role !== 'assistant') {
                return next;
            }
            const updated = {
                ...last,
                text: replaceEmpty ? (last.text || text) : last.text + text,
            };
            next[next.length - 1] = updated;
            return next;
        });
    }

    function setArticles(articles) {
        setMessages((current) => {
            const next = [...current];
            const last = next[next.length - 1];
            if (!last || last.role !== 'assistant') {
                return next;
            }
            next[next.length - 1] = { ...last, articles };
            return next;
        });
    }
}

function linkEmail(text) {
    if (!text || !text.includes(SUPPORT_EMAIL)) {
        return text;
    }
    const parts = text.split(SUPPORT_EMAIL);
    const nodes = [];
    parts.forEach((part, index) => {
        if (part) {
            nodes.push(part);
        }
        if (index < parts.length - 1) {
            nodes.push(
                <a key={`${index}-mail`} href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
            );
        }
    });
    return nodes;
}

export default SupportChat;
